import { Component, OnInit, inject } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { DropdownModule } from 'primeng/dropdown';
import { ProgressBarModule } from 'primeng/progressbar';
import { MessagesModule } from 'primeng/messages';
import { TagModule } from 'primeng/tag';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';

import { InMemoryCacheService, MessageType, UtilsService } from 'src/app/common';
import { PermissionsService } from 'src/app/common/services/permissions.service';
import { OpportunitiesApiService } from '../opportunities-api.service';
import { OpportunitiesKpiBarComponent } from '../opportunities-kpi-bar/opportunities-kpi-bar.component';
import { OpportunityDetailPanelComponent } from '../opportunity-detail-panel/opportunity-detail-panel.component';
import {
  OpportunityItem,
  OpportunitySourceCheck,
  OpportunitySourceStatus,
  OpportunityStatus,
  OpportunityUpdateRequest,
  OpportunityRefreshRequest,
  OpportunityRefreshResult,
  OpportunitiesWorkspaceResponse,
  UrlInspectionResult,
} from '../models/opportunity.models';

interface TabDefinition {
  key: string;
  label: string;
  filter: ((item: OpportunityItem) => boolean) | null;
}

@Component({
  selector: 'gig-opportunities-workspace',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    TableModule,
    DropdownModule,
    ProgressBarModule,
    MessagesModule,
    TagModule,
    ConfirmDialogModule,
    DialogModule,
    InputTextModule,
    OpportunitiesKpiBarComponent,
    OpportunityDetailPanelComponent,
  ],
  providers: [DecimalPipe],
  templateUrl: './opportunities-workspace.component.html',
  styleUrl: './opportunities-workspace.component.scss',
})
export class OpportunitiesWorkspaceComponent implements OnInit {
  private readonly apiService = inject(OpportunitiesApiService);
  private readonly cache = inject(InMemoryCacheService);
  private readonly permissionsService = inject(PermissionsService);

  public readonly utils = inject(UtilsService);

  // --- State ---
  public allItems: OpportunityItem[] = [];
  public filteredItems: OpportunityItem[] = [];
  public sourceChecks: OpportunitySourceCheck[] = [];
  public selectedItem: OpportunityItem | null = null;
  public isPanelVisible = false;
  public isLoading = false;
  public loadError = false;
  public isRefreshing = false;
  public isInspecting = false;
  public activeTabIndex = 0;
  public showInspectDialog = false;
  public inspectUrlInput = '';
  public inspectResult: UrlInspectionResult | null = null;
  public inspectSiteUrl = '';
  public selectedSitemapUrl = '';

  // --- Permissions ---
  public canEdit = false;
  public canRefresh = false;

  // --- Dropdown options ---
  public readonly statusOptions: { label: string; value: OpportunityStatus }[] = [
    { label: 'New', value: 'NEW' },
    { label: 'Accepted', value: 'ACCEPTED' },
    { label: 'In Progress', value: 'IN_PROGRESS' },
    { label: 'Done', value: 'DONE' },
    { label: 'Ignored', value: 'IGNORED' },
  ];

  // --- Tab definitions ---
  public readonly tabs: TabDefinition[] = [
    { key: 'ALL', label: 'All', filter: null },
    { key: 'TOP_IMPACTFUL', label: 'Top Impactful', filter: (i) => i.businessImpactScore === 'HIGH' },
    { key: 'QUICK_WINS', label: 'Quick Wins', filter: (i) => i.effort === 'LOW' },
    {
      key: 'CONTENT_GAPS',
      label: 'Content Gaps',
      filter: (i) => ['CONTENT_GAP', 'CANNIBALIZATION', 'DECAY'].includes(i.type),
    },
    { key: 'TECHNICAL_FIXES', label: 'Technical Fixes', filter: (i) => i.type === 'TECHNICAL_FIX' },
    { key: 'BACKLINK_GAPS', label: 'Backlink Gaps', filter: (i) => i.type === 'BACKLINK_GAP' },
    { key: 'LOCAL_SEO', label: 'Local SEO', filter: (i) => i.type === 'LOCAL' },
    {
      key: 'REVIEWS',
      label: 'Reviews',
      filter: (i) => ['REVIEW_REPUTATION_RISK', 'REVIEW_RATING_DECLINE'].includes(i.type),
    },
    {
      key: 'COMPETITORS',
      label: 'Competitors',
      filter: (i) => ['COMPETITOR_CONTENT_GAP', 'LINK_INTERSECT', 'COMPETITOR_RANK_LOSS'].includes(i.type),
    },
    {
      key: 'CTR_RANKING',
      label: 'CTR / Ranking',
      filter: (i) => ['CTR_LIFT', 'NEAR_PAGE_ONE'].includes(i.type),
    },
    {
      key: 'GA4_AWARE',
      label: 'GA4-Aware',
      filter: (i) => ['TRAFFIC_QUALITY_LEAK', 'CAMPAIGN_HALO', 'BUSINESS_IMPACT_DECAY'].includes(i.type),
    },
  ];

  public async ngOnInit(): Promise<void> {
    await this.resolveSelectedProject();
    const projectId = this.cache.currentProject?.id;
    this.canEdit = this.permissionsService.can('Opportunities.Edit', projectId);
    this.canRefresh = this.permissionsService.can('Opportunities.Refresh', projectId);
    await this.loadOpportunities();
  }

  // --- Public methods ---

  public tabCount(tab: TabDefinition): number {
    if (!tab.filter) {
      return this.openItems.length;
    }
    return this.openItems.filter(tab.filter).length;
  }

  public onTabChange(index: number): void {
    this.activeTabIndex = index;
    this.applyTabFilter();
  }

  public getTabIconClass(index: number): string {
    const iconByIndex: Record<number, string> = {
      0: 'pi pi-list',
      1: 'pi pi-chart-line',
      2: 'pi pi-bolt',
      3: 'pi pi-file-edit',
      4: 'pi pi-cog',
      5: 'pi pi-link',
      6: 'pi pi-map-marker',
      7: 'pi pi-star',
      8: 'pi pi-users',
      9: 'pi pi-chart-bar',
      10: 'pi pi-google',
    };

    return iconByIndex[index] ?? 'pi pi-tag';
  }

  public getSourceStatusClass(status: OpportunitySourceStatus): string {
    const statusMap: Record<OpportunitySourceStatus, string> = {
      CONNECTED: 'connected',
      STALE: 'stale',
      PARTIAL: 'partial',
      MISSING: 'missing',
      ERROR: 'error',
    };

    return statusMap[status] ?? 'missing';
  }

  public getSourceStatusLabel(status: OpportunitySourceStatus): string {
    switch (status) {
      case 'CONNECTED':
        return 'Connected';
      case 'STALE':
        return 'Stale';
      case 'PARTIAL':
        return 'Partial';
      case 'MISSING':
        return 'Missing';
      case 'ERROR':
        return 'Error';
      default:
        return status;
    }
  }

  public getSourceIconClass(sourceKey: OpportunitySourceCheck['sourceKey']): string {
    const iconMap: Record<OpportunitySourceCheck['sourceKey'], string> = {
      GSC: 'pi pi-search',
      GA4: 'pi pi-chart-line',
      SITE_AUDIT: 'pi pi-globe',
      BACKLINKS: 'pi pi-link',
      REVIEWS: 'pi pi-star',
      REVIEW_AI: 'pi pi-sparkles',
      COMPETITORS: 'pi pi-users',
    };

    return iconMap[sourceKey] ?? 'pi pi-database';
  }

  public getEmptyStateMessage(): string {
    const activeTab = this.tabs[this.activeTabIndex];
    const competitorStatus = this.getSourceStatus('COMPETITORS');
    const backlinkStatus = this.getSourceStatus('BACKLINKS');
    const ga4Status = this.getSourceStatus('GA4');
    const siteAuditStatus = this.getSourceStatus('SITE_AUDIT');
    const reviewStatus = this.getSourceStatus('REVIEWS');
    const reviewAiStatus = this.getSourceStatus('REVIEW_AI');

    switch (activeTab?.key) {
      case 'BACKLINK_GAPS':
        if (backlinkStatus === 'MISSING' || backlinkStatus === 'ERROR') {
          return 'No backlink opportunities yet. Backlink data is not connected.';
        }
        return 'No backlink opportunities found.';
      case 'TECHNICAL_FIXES':
        if (siteAuditStatus === 'MISSING') {
          return 'No technical opportunities yet. Run a Site Audit to generate technical recommendations.';
        }
        if (siteAuditStatus === 'STALE' || siteAuditStatus === 'PARTIAL') {
          return 'Technical opportunities are limited because Site Audit data is stale or incomplete.';
        }
        return 'No technical opportunities found.';
      case 'REVIEWS':
        if (reviewStatus === 'MISSING') {
          return 'No review opportunities yet. Connect and run Reviews in Project Settings to enable reputation recommendations.';
        }
        if (reviewStatus === 'STALE' || reviewStatus === 'PARTIAL' || reviewStatus === 'ERROR') {
          return 'Review opportunities are limited because review scan data is stale or incomplete.';
        }
        if (reviewAiStatus === 'MISSING' || reviewAiStatus === 'PARTIAL') {
          return 'No review opportunities found yet. Review scans are connected, and enabling Review AI can add richer insight-driven recommendations.';
        }
        return 'No review opportunities found.';
      case 'COMPETITORS':
        if (competitorStatus === 'MISSING') {
          return 'No competitor opportunities yet. Add competitor domains in Project Settings to enable competitor analysis.';
        }
        if (competitorStatus === 'STALE' || competitorStatus === 'PARTIAL' || competitorStatus === 'ERROR') {
          return 'Competitor opportunities are limited because competitor data is stale or incomplete.';
        }
        return 'No competitor opportunities found.';
      case 'GA4_AWARE':
        if (ga4Status === 'MISSING' || ga4Status === 'ERROR') {
          return 'No GA4-aware opportunities yet. Connect Google Analytics to enable engagement-based recommendations.';
        }
        if (ga4Status === 'STALE' || ga4Status === 'PARTIAL') {
          return 'GA4-aware opportunities are limited because analytics data is stale or incomplete.';
        }
        return 'No GA4-aware opportunities found.';
      default:
        return 'No opportunities found - run an analysis to populate this list.';
    }
  }

  public onRowSelect(item: OpportunityItem): void {
    this.selectedItem = item;
    this.isPanelVisible = true;
  }

  public onPanelClose(): void {
    this.isPanelVisible = false;
  }

  public async onPanelSave(event: { item: OpportunityItem; request: OpportunityUpdateRequest }): Promise<void> {
    const { item, request } = event;
    const { data, error } = await this.apiService.updateOpportunity(item.id, item.projectId, request);
    if (error) {
      this.utils.errorMessage(MessageType.TOAST, 'Error', 'Failed to save opportunity.');
      return;
    }
    const updated = data as OpportunityItem;
    const idx = this.allItems.findIndex(i => i.id === updated.id);
    if (idx !== -1) {
      this.allItems[idx] = updated;
      this.allItems = [...this.allItems];
    }
    this.applyTabFilter();
    this.isPanelVisible = false;
    this.utils.successMessage(MessageType.TOAST, 'Saved', 'Opportunity updated successfully.');
  }

  public async onInlineStatusChange(item: OpportunityItem, newStatus: OpportunityStatus): Promise<void> {
    const prev = item.status;
    item.status = newStatus;
    const { error } = await this.apiService.updateOpportunity(item.id, item.projectId, { status: newStatus });
    if (error) {
      item.status = prev;
      this.utils.errorMessage(MessageType.TOAST, 'Error', 'Failed to update status.');
    }
  }

  public onRunAnalysis(): void {
    this.utils.confirm(
      'Run Analysis',
      'This will re-analyse all SEO data for this project. Continue?',
      async () => {
        await this.executeRefresh();
      }
    );
  }

  public async onOpenInspectDialog(): Promise<void> {
    await this.resolveSelectedProject();
    this.inspectResult = null;
    this.selectedSitemapUrl = '';
    this.inspectUrlInput = this.deriveDefaultInspectionUrl();
    this.inspectSiteUrl = this.resolveSearchConsoleSiteUrl();

    if (!this.inspectSiteUrl) {
      this.utils.errorMessage(
        MessageType.TOAST,
        'Missing Search Console Site URL',
        'Set the Google Search Console Site URL in Project Settings before inspecting URLs.'
      );
      return;
    }

    this.showInspectDialog = true;
  }

  public get sitemapUrlOptions(): { label: string; value: string }[] {
    const sitemapUrls = this.getGoogleSitemapUrls();
    if (!Array.isArray(sitemapUrls)) {
      return [];
    }

    return sitemapUrls
      .map((url: unknown) => (typeof url === 'string' ? url.trim() : ''))
      .filter((url: string) => !!url)
      .map((url: string) => ({ label: url, value: url }));
  }

  public onSitemapUrlSelected(): void {
    if (!this.selectedSitemapUrl) {
      return;
    }

    this.inspectUrlInput = this.selectedSitemapUrl;
  }

  public async onInspectUrl(): Promise<void> {
    const projectId = this.cache.currentProject?.id;
    const siteUrl = this.resolveSearchConsoleSiteUrl();
    const inspectionUrl = (this.inspectUrlInput ?? '').trim();

    if (!projectId || !siteUrl || !inspectionUrl) {
      this.utils.errorMessage(
        MessageType.TOAST,
        'Missing Required Fields',
        'Project, Site URL, and Inspection URL are all required.'
      );
      return;
    }

    this.isInspecting = true;
    const { data, error } = await this.apiService.inspectUrl({
      projectId,
      siteUrl,
      inspectionUrl,
      languageCode: 'en-US',
    });

    if (error) {
      this.utils.errorMessage(MessageType.TOAST, 'Inspection Failed', 'Unable to inspect URL. Please try again.');
      this.inspectResult = null;
      this.isInspecting = false;
      return;
    }

    this.inspectResult = data;
    this.isInspecting = false;
  }

  public getTypeBadgeClass(type: string): string {
    const severityMap: Record<string, 'green' | 'orange' | 'red'> = {
      CTR_LIFT: 'green',
      NEAR_PAGE_ONE: 'green',
      LOCAL: 'green',
      REVIEW_RATING_DECLINE: 'orange',
      CONTENT_GAP: 'orange',
      CANNIBALIZATION: 'orange',
      DECAY: 'orange',
      BACKLINK_GAP: 'orange',
      COMPETITOR_CONTENT_GAP: 'orange',
      LINK_INTERSECT: 'orange',
      TECHNICAL_FIX: 'red',
      REVIEW_REPUTATION_RISK: 'red',
      COMPETITOR_RANK_LOSS: 'red',
      TRAFFIC_QUALITY_LEAK: 'red',
      CAMPAIGN_HALO: 'red',
      BUSINESS_IMPACT_DECAY: 'red',
    };

    return `badge ${severityMap[type] ?? 'orange'}`;
  }

  public getEffortBadgeClass(effort: string): string {
    const map: Record<string, 'green' | 'orange' | 'red'> = {
      LOW: 'green',
      MEDIUM: 'orange',
      HIGH: 'red',
    };

    return `badge ${map[effort] ?? 'orange'}`;
  }

  public getImpactBadgeClass(score?: string): string {
    const map: Record<string, 'green' | 'orange' | 'red'> = {
      LOW: 'green',
      MEDIUM: 'orange',
      HIGH: 'red',
    };

    if (!score) {
      return 'badge orange';
    }

    return `badge ${map[score] ?? 'orange'}`;
  }

  public getTypeLabel(type: string): string {
    const labels: Record<string, string> = {
      CTR_LIFT: 'CTR Lift',
      NEAR_PAGE_ONE: 'Near Page 1',
      CONTENT_GAP: 'Content Gap',
      CANNIBALIZATION: 'Cannibalization',
      DECAY: 'Decay',
      BACKLINK_GAP: 'Backlink Gap',
      TECHNICAL_FIX: 'Technical Fix',
      LOCAL: 'Local SEO',
      REVIEW_REPUTATION_RISK: 'Review Reputation Risk',
      REVIEW_RATING_DECLINE: 'Review Rating Decline',
      COMPETITOR_CONTENT_GAP: 'Competitor Content Gap',
      LINK_INTERSECT: 'Link Intersect',
      COMPETITOR_RANK_LOSS: 'Competitor Rank Loss',
      TRAFFIC_QUALITY_LEAK: 'Quality Leak',
      CAMPAIGN_HALO: 'Campaign Halo',
      BUSINESS_IMPACT_DECAY: 'Impact Decay',
    };
    return labels[type] ?? type;
  }

  public getEffortSeverity(effort: string): 'success' | 'warning' | 'danger' {
    const map: Record<string, 'success' | 'warning' | 'danger'> = {
      LOW: 'success',
      MEDIUM: 'warning',
      HIGH: 'danger',
    };
    return map[effort] ?? ('info' as any);
  }

  public getImpactSeverity(score?: string): 'success' | 'warning' | 'danger' | 'secondary' {
    const map: Record<string, 'success' | 'warning' | 'danger' | 'secondary'> = {
      HIGH: 'danger',
      MEDIUM: 'warning',
      LOW: 'success',
    };
    if (!score || !map[score]) {
      return 'secondary';
    }
    return map[score];
  }

  public isGaConnected(): boolean {
    // Check if at least one item has GA connected and no global error
    return this.allItems.some(item => item.gaContext?.isGaConnected === true);
  }

  public getGaErrorMessage(): string | null {
    // Return the first GA error message found
    const errorItem = this.allItems.find(item => item.gaContext?.gaErrorMessage);
    return errorItem?.gaContext?.gaErrorMessage ?? null;
  }

  // --- Private methods ---

  private get openItems(): OpportunityItem[] {
    return this.allItems.filter(i => i.status !== 'DONE' && i.status !== 'IGNORED');
  }

  private async loadOpportunities(): Promise<void> {
    const projectId = this.cache.currentProject?.id;
    if (!projectId) {
      return;
    }
    this.isLoading = true;
    this.loadError = false;
    const { data, error } = await this.apiService.getOpportunities(projectId);
    if (error) {
      this.loadError = true;
    } else {
      const workspaceResponse = this.normalizeWorkspaceResponse(data);
      const items = workspaceResponse.items;
      this.allItems = [...items].sort((a, b) => a.priority - b.priority);
      this.sourceChecks = this.buildSourceChecks(workspaceResponse);
      this.applyTabFilter();
    }
    this.isLoading = false;
  }

  private applyTabFilter(): void {
    const tab = this.tabs[this.activeTabIndex];
    this.filteredItems = tab.filter
      ? this.allItems.filter(tab.filter)
      : [...this.allItems];
  }

  private async executeRefresh(): Promise<void> {
    const projectId = this.cache.currentProject?.id;
    if (!projectId) {
      return;
    }
    const orgId = this.resolveOrganizationId();
    if (orgId <= 0) {
      this.utils.errorMessage(MessageType.TOAST, 'Analysis Failed', 'Organization context is missing. Please refresh the page and try again.');
      return;
    }
    this.isRefreshing = true;
    const request: OpportunityRefreshRequest = {
      orgId,
      siteUrl: this.cache.currentProject?.domain,
      propertyId: localStorage.getItem('googlePropertyId') ?? undefined,
    };
    const { data, error } = await this.apiService.refreshOpportunities(projectId, request);
    if (error) {
      if (error?.status === 401) {
        this.utils.errorMessage(
          MessageType.TOAST,
          'Google Reconnection Required',
          'Your Google account connection has expired. Please reconnect Google Search Console and try again.'
        );
      } else {
        this.utils.errorMessage(MessageType.TOAST, 'Analysis Failed', 'Please try again.');
      }
    } else {
      const result = data as OpportunityRefreshResult;
      const count = result.createdCount;
      const plural = count === 1 ? 'y' : 'ies';
      this.utils.successMessage(
        MessageType.TOAST,
        'Analysis Complete',
        `${count} new opportunit${plural} found from ${result.examinedRows} rows.`
      );
      await this.loadOpportunities();
    }
    this.isRefreshing = false;
  }

  private resolveOrganizationId(): number {
    const organizationIdFromContext = Number(this.cache?.organization?.id);
    if (Number.isFinite(organizationIdFromContext) && organizationIdFromContext > 0) {
      return organizationIdFromContext;
    }

    const organizationIdFromUser = Number(this.cache?.user?.currentOrganizationId);
    if (Number.isFinite(organizationIdFromUser) && organizationIdFromUser > 0) {
      return organizationIdFromUser;
    }

    return 0;
  }

  private resolveSearchConsoleSiteUrl(): string {
    const explicitSiteUrl = (this.cache.selectedProject?.externalProviders?.google?.siteUrl ?? '').trim();
    if (explicitSiteUrl) {
      return explicitSiteUrl;
    }

    const sitemapCandidate = (this.selectedSitemapUrl || this.getGoogleSitemapUrls()[0] || '').trim();
    if (sitemapCandidate) {
      const siteUrlFromSitemap = this.tryGetOriginFromUrl(sitemapCandidate);
      if (siteUrlFromSitemap) {
        return siteUrlFromSitemap;
      }
    }

    const domain = (this.cache.currentProject?.domain ?? '').trim();
    if (!domain) {
      return '';
    }

    if (/^https?:\/\//i.test(domain)) {
      return domain;
    }

    return `https://${domain}`;
  }

  private deriveDefaultInspectionUrl(): string {
    const evidenceUrl = this.selectedItem?.evidence?.find((evidence) => !!evidence.url)?.url;
    if (evidenceUrl) {
      return evidenceUrl;
    }

    const domain = (this.cache.currentProject?.domain ?? '').trim();
    if (!domain) {
      return '';
    }

    if (/^https?:\/\//i.test(domain)) {
      return domain;
    }

    return `https://${domain}`;
  }

  private getGoogleSitemapUrls(): string[] {
    const sitemapUrls = this.cache.selectedProject?.externalProviders?.google?.sitemapUrls;
    if (!Array.isArray(sitemapUrls)) {
      return [];
    }

    return sitemapUrls
      .map((url: unknown) => (typeof url === 'string' ? url.trim() : ''))
      .filter((url: string) => !!url);
  }

  private tryGetOriginFromUrl(value: string): string {
    try {
      const parsed = new URL(value);
      return parsed.origin;
    } catch {
      return '';
    }
  }

  private async resolveSelectedProject(): Promise<any> {
    if (this.cache?.selectedProject?.id) {
      return this.cache.selectedProject;
    }

    const currentProject = this.cache.currentProject;
    if (!currentProject?.id) {
      return null;
    }

    const orgId = this.cache.organization?.id ?? this.cache.user?.currentOrganizationId;

    let result: any;
    if (orgId) {
      result = await this.cache.getProjectByOrg(currentProject.id, String(orgId));
    } else {
      result = await this.cache.getProject(currentProject.id, this.cache.userId);
    }

    if (result?.data) {
      this.cache.selectedProject = result.data;
      return result.data;
    }

    return null;
  }

  private normalizeWorkspaceResponse(data: OpportunitiesWorkspaceResponse | null): OpportunitiesWorkspaceResponse {
    return {
      items: Array.isArray(data?.items) ? data.items : [],
      sourceChecks: Array.isArray(data?.sourceChecks) ? data.sourceChecks : [],
    };
  }

  private buildSourceChecks(workspaceResponse: OpportunitiesWorkspaceResponse): OpportunitySourceCheck[] {
    const derivedChecks = this.getDerivedSourceChecks();
    const apiChecks = workspaceResponse.sourceChecks ?? [];
    const mergedChecks = new Map<string, OpportunitySourceCheck>();

    for (const check of derivedChecks) {
      mergedChecks.set(check.sourceKey, check);
    }

    for (const check of apiChecks) {
      mergedChecks.set(check.sourceKey, check);
    }

    return Array.from(mergedChecks.values());
  }

  private getDerivedSourceChecks(): OpportunitySourceCheck[] {
    const checks: OpportunitySourceCheck[] = [];
    const googleProvider = this.cache.selectedProject?.externalProviders?.google;
    const hasGsc = !!(googleProvider?.siteUrl || googleProvider?.propertyId);
    const competitorCount = this.getSavedCompetitorsCount();
    const isGaConnected = this.isGaConnected() || !!(googleProvider?.accessToken && googleProvider?.refreshToken && googleProvider?.propertyId);
    const gaErrorMessage = this.getGaErrorMessage();

    checks.push({
      sourceKey: 'GSC',
      label: 'Search Console',
      status: hasGsc ? 'CONNECTED' : 'MISSING',
      detail: hasGsc
        ? 'Search Console source is configured for this project.'
        : 'Add a Search Console site URL or property ID in Project Settings to enable ranking opportunities.',
      actionLabel: hasGsc ? undefined : 'Connect Search Console',
    });

    checks.push({
      sourceKey: 'GA4',
      label: 'Google Analytics',
      status: isGaConnected ? 'CONNECTED' : (gaErrorMessage ? 'ERROR' : 'MISSING'),
      detail: isGaConnected
        ? 'Google Analytics is connected for GA4-aware opportunities.'
        : (gaErrorMessage || 'Connect Google Analytics to enable engagement-based recommendations.'),
      actionLabel: isGaConnected ? undefined : 'Connect Google Analytics',
    });

    checks.push({
      sourceKey: 'COMPETITORS',
      label: 'Competitors',
      status: competitorCount > 0 ? 'CONNECTED' : 'MISSING',
      detail: competitorCount > 0
        ? `${competitorCount} saved competitor${competitorCount === 1 ? '' : 's'} available for analysis.`
        : 'Add competitor domains in Project Settings to enable competitor opportunities.',
      actionLabel: competitorCount > 0 ? undefined : 'Add Competitors',
    });

    return checks;
  }

  private getSavedCompetitorsCount(): number {
    const selectedProjectCompetitors = this.cache.selectedProject?.competitors;
    if (Array.isArray(selectedProjectCompetitors) && selectedProjectCompetitors.length > 0) {
      return selectedProjectCompetitors.filter((competitor: any) => !!competitor?.domain || !!competitor?.website).length;
    }

    const siteInformationCompetitors = this.cache.selectedProject?.siteInfoCompetitors;
    if (Array.isArray(siteInformationCompetitors) && siteInformationCompetitors.length > 0) {
      return siteInformationCompetitors.filter((competitor: any) => !!competitor?.competitor_domain).length;
    }

    return 0;
  }

  private getSourceStatus(sourceKey: OpportunitySourceCheck['sourceKey']): OpportunitySourceStatus | null {
    const sourceCheck = this.sourceChecks.find((check) => check.sourceKey === sourceKey);
    return sourceCheck?.status ?? null;
  }
}
